import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import moment from "moment";
import { intl } from "LocaleWrapper";
import { useLazyQuery, useMutation } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";

import { FormGroup, Select } from "@cofano/cck";

import { FormDialog } from "components/dialogs/Dialogs";
import { FormErrorMessage } from "components/form/FormErrorMessage";
import DatePicker from "components/cck/DatePicker";

import { LocationSelectOption } from "view/locations/components/selects/LocationSelect";
import { createConnectingShipBerthingSchema, CreateConnectingShipBerthingSchemaInput, CreateConnectingShipBerthingSchemaOutput } from "view/connectingVisits/utils/connectingvisit.schema";

import { CreateBerthingDocument, LinkActionToBerthingDocument } from "__generated__/view/connectingVisits/graphql/connectingVisit.mutations.generated";
import { GetConnectingShipPortVisitDocument } from "__generated__/view/connectingVisits/graphql/connectingVisit.queries.generated";

const CreateConnectingShipBerthingDialog = ({ action, open, onClose, visitId, refetch }: {
	open: boolean,
	onClose: () => void,
	visitId: number,
	action: {
		id: number | null;
		location: LocationSelectOption | null;
		start: number | null;
		end: number | null;
	},
	refetch: () => void,
}) => {

	const [ getConnectingVisit ] = useLazyQuery(GetConnectingShipPortVisitDocument);

	const [ createBerthing, { loading: createBerthingLoading } ] = useMutation(CreateBerthingDocument);
	const [ linkActionToBerthing, { loading: linkActionToBerthingLoading } ] = useMutation(LinkActionToBerthingDocument);

	const [ locations, setLocations ] = useState<LocationSelectOption[]>([]);

	const loading = createBerthingLoading || linkActionToBerthingLoading;

	const defaultValues = {
		location: null,
		from: null,
		to: null,
	};

	const { control, handleSubmit, reset, setValue, getValues } = useForm<CreateConnectingShipBerthingSchemaInput, unknown, CreateConnectingShipBerthingSchemaOutput>({
		resolver: zodResolver(createConnectingShipBerthingSchema),
		defaultValues: defaultValues,
	});

	const handleOnClose = () => {
		onClose();
		reset(defaultValues);
	};

	const save = (data: CreateConnectingShipBerthingSchemaOutput) => {
		createBerthing({
			variables: {
				input: {
					visit: {
						id: visitId,
					},
					location: {
						id: data.location.id,
					},
					window: {
						from: data.from,
						to: data.to,
					},
				},
			},
		}).then(({ data }) => {
			if (data?.createBerthing?.id && action?.id) {
				linkActionToBerthing({
					variables: {
						actionId: action.id,
						visitId: visitId,
						berthingId: data.createBerthing.id,
					},
				}).then(() => {
					refetch();
					handleOnClose();
				});
			} else {
				refetch();
				handleOnClose();
			}
		});
	};

	useEffect(() => {
		reset({
			location: action?.location ?? null,
			from: action?.start ?? null,
			to: action?.end ?? null,
		});
	}, [ action ]);

	useEffect(() => {
		if (open) {
			getConnectingVisit({
				variables: {
					id: visitId,
				},
			}).then(({ data }) => {
				const location = data?.connectingShipPortVisit?.location;

				if (location) {
					const locations = [ location, ...location?.subLocations ?? [] ];
					setLocations(locations);
				}
			});
		}
	}, [ open, visitId ]);

	return (
		<FormDialog
			open={open}
			onClose={handleOnClose}
			title={intl.formatMessage({ id: "CONNECTING_VISIT_CREATE_BERTHING" })}
			form='shipBerthingForm'
			loading={loading}>
			<form onSubmit={(event) => void handleSubmit(save)(event)} id="shipBerthingForm">
				<FormGroup label={intl.formatMessage({ id: "LOCATION" })} overflow="hidden" required>
					<Controller
						name="location"
						control={control}
						render={({ field, formState }) => (
							<React.Fragment>
								<Select
									{ ...field }
									fetchOptions={locations}
									getOptionLabel={option => option?.description || ""} />
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)} />
				</FormGroup>

				<FormGroup label={intl.formatMessage({ id: "FROM" })} overflow="hidden">
					<Controller
						name="from"
						control={control}
						render={({ field }) => (
							<DatePicker
								{ ...field }
								showTimeInput
								onChange={(newFrom) => {
									const currentFrom = getValues("from");
									const currentTo = getValues("to");
									field.onChange(newFrom);

									if (newFrom == null) {
										setValue("to", null);
										return;
									}

									if (currentFrom != null && currentTo != null) {
										const diff = currentTo - currentFrom;
										const newTo = moment(newFrom + diff)
											.add(1, "second")
											.seconds(0)
											.milliseconds(0)
											.valueOf();
										setValue("to", newTo);
									}
								}} />
						)} />
				</FormGroup>

				<FormGroup label={intl.formatMessage({ id: "TO" })} overflow="hidden">
					<Controller
						name="to"
						control={control}
						render={({ field }) => <DatePicker { ...field } showTimeInput />} />
				</FormGroup>
			</form>
		</FormDialog>
	);
};

export default CreateConnectingShipBerthingDialog;
